import { useState } from "react";
import { Plus, Edit, Trash2, Search, ArrowLeftRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { PaginacaoRodape } from "@/components/PaginacaoRodape";
import { type TamanhoPagina } from "@/hooks/usePaginacaoLocal";
import { useDebounce } from "@/hooks/useDebounce";
import { usePlanoContas } from "@/features/contabil/hooks/usePlanoContas";
import {
  useOperacoesFluxoCaixa,
  type OperacaoFluxoCaixa,
  type SalvarOperacaoFluxoCaixaRequest,
} from "@/features/financeiro/hooks/useOperacoesFluxoCaixa";

const EMPTY: SalvarOperacaoFluxoCaixaRequest = {
  descricao: "",
  contaContabilDebitoId: 0,
  contaContabilCreditoId: 0,
  observacao: "",
  isAtivo: true,
};

export default function OperacoesFluxoCaixa() {
  const [buscaInput, setBuscaInput] = useState("");
  const [pagina, setPagina] = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState<TamanhoPagina>(20);
  const busca = useDebounce(buscaInput, 400);

  const { query, criar, atualizar, excluir } = useOperacoesFluxoCaixa(busca, pagina, tamanhoPagina);
  const { plano } = usePlanoContas();

  const itens = query.data?.itens ?? [];
  const total = query.data?.total ?? 0;
  const totalPaginas = query.data?.totalPaginas ?? 1;
  const primeiroItem = total === 0 ? 0 : (pagina - 1) * tamanhoPagina + 1;
  const ultimoItem = Math.min(pagina * tamanhoPagina, total);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [form, setForm] = useState<SalvarOperacaoFluxoCaixaRequest>(EMPTY);

  const set = (campo: keyof SalvarOperacaoFluxoCaixaRequest, valor: unknown) =>
    setForm((f) => ({ ...f, [campo]: valor }));

  const contaLabel = (id: number) => {
    const c = plano?.find((p) => p.id === id);
    return c ? `${c.codigoPlanoConta ?? ""} ${c.descricaoConta ?? ""}`.trim() : "—";
  };

  const openNew = () => { setEditId(null); setForm(EMPTY); setDialogOpen(true); };

  const openEdit = (o: OperacaoFluxoCaixa) => {
    setEditId(o.id);
    setForm({
      descricao: o.descricao,
      observacao: o.observacao ?? "",
      contaContabilDebitoId: o.contaContabilDebitoId,
      contaContabilCreditoId: o.contaContabilCreditoId,
      isAtivo: o.isAtivo,
    });
    setDialogOpen(true);
  };

  const handleSubmit = () => {
    if (editId) atualizar.mutate({ id: editId, req: form }, { onSuccess: () => setDialogOpen(false) });
    else criar.mutate(form, { onSuccess: () => setDialogOpen(false) });
  };

  const handleDelete = () => {
    if (deleteId) excluir.mutate(deleteId, { onSuccess: () => setDeleteId(null) });
  };

  const podeGravar =
    form.descricao.trim() !== "" &&
    form.contaContabilDebitoId > 0 &&
    form.contaContabilCreditoId > 0;

  const contasAtivas = (plano ?? []).filter((c) => !c.inativo);

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ArrowLeftRight className="h-6 w-6" /> Operações de Fluxo de Caixa
          </h1>
          <p className="text-muted-foreground">Mapeia cada operação financeira a contas contábeis de débito e crédito.</p>
        </div>
        <Button onClick={openNew}><Plus className="h-4 w-4 mr-2" />Nova Operação</Button>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Buscar por descrição..."
          className="pl-9"
          value={buscaInput}
          onChange={(e) => { setBuscaInput(e.target.value); setPagina(1); }}
        />
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Descrição</TableHead>
              <TableHead>Conta Débito</TableHead>
              <TableHead>Conta Crédito</TableHead>
              <TableHead className="text-center w-20">Ativo</TableHead>
              <TableHead className="w-24">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {query.isLoading ? (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Carregando...</TableCell></TableRow>
            ) : itens.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Nenhuma operação cadastrada.</TableCell></TableRow>
            ) : (
              itens.map((o) => (
                <TableRow key={o.id}>
                  <TableCell className="font-medium">{o.descricao}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{contaLabel(o.contaContabilDebitoId)}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{contaLabel(o.contaContabilCreditoId)}</TableCell>
                  <TableCell className="text-center">
                    <Badge variant={o.isAtivo ? "default" : "secondary"}>{o.isAtivo ? "Sim" : "Não"}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(o)}><Edit className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => setDeleteId(o.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {query.data && (
        <PaginacaoRodape
          pagina={pagina}
          totalPaginas={totalPaginas}
          totalItens={total}
          primeiroItem={primeiroItem}
          ultimoItem={ultimoItem}
          tamanhoPagina={tamanhoPagina}
          onPaginaChange={setPagina}
          onTamanhoPaginaChange={(t) => { setTamanhoPagina(t); setPagina(1); }}
        />
      )}

      {/* Dialog criar/editar */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editId ? "Editar Operação" : "Nova Operação"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <Label>Descrição *</Label>
              <Input value={form.descricao} maxLength={50} onChange={(e) => set("descricao", e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>Conta Contábil de Débito *</Label>
              <Select
                value={form.contaContabilDebitoId ? String(form.contaContabilDebitoId) : ""}
                onValueChange={(v) => set("contaContabilDebitoId", Number(v))}
              >
                <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                <SelectContent>
                  {contasAtivas.map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>{c.codigoPlanoConta} — {c.descricaoConta}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Conta Contábil de Crédito *</Label>
              <Select
                value={form.contaContabilCreditoId ? String(form.contaContabilCreditoId) : ""}
                onValueChange={(v) => set("contaContabilCreditoId", Number(v))}
              >
                <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                <SelectContent>
                  {contasAtivas.map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>{c.codigoPlanoConta} — {c.descricaoConta}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Observação</Label>
              <Textarea value={form.observacao ?? ""} maxLength={200} rows={2} onChange={(e) => set("observacao", e.target.value)} />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.isAtivo} onCheckedChange={(v) => set("isAtivo", v)} />
              <Label>Ativo</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSubmit} disabled={!podeGravar || criar.isPending || atualizar.isPending}>
              {criar.isPending || atualizar.isPending ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog excluir */}
      <AlertDialog open={deleteId !== null} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir operação?</AlertDialogTitle>
            <AlertDialogDescription>Esta ação não pode ser desfeita.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
